import { Heart } from "lucide-react";
import { SiFacebook, SiInstagram, SiLinkedin } from "react-icons/si";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { t } from "@/lib/translations";

const WA = "https://wa.me/243835666498?text=Bonjour%20FGNB%2C%20je%20souhaite%20vous%20rejoindre%20!";
const EMAIL = "naelbeko@gmail.com";

const PARTNERS = [
  { name:"UNESCO",   logo:"https://upload.wikimedia.org/wikipedia/commons/thumb/b/bf/UNESCO_logo.svg/180px-UNESCO_logo.svg.png",   url:"https://www.unesco.org" },
  { name:"UNICEF",   logo:"https://upload.wikimedia.org/wikipedia/commons/thumb/1/1a/UNICEF_Logo.png/180px-UNICEF_Logo.png",        url:"https://www.unicef.org" },
  { name:"Gouvernement RDC", logo:"https://upload.wikimedia.org/wikipedia/commons/thumb/6/6f/Flag_of_the_Democratic_Republic_of_the_Congo.svg/120px-Flag_of_the_Democratic_Republic_of_the_Congo.svg.png", url:"https://www.gouvernement.cd" },
  { name:"Ville de Kinshasa", logo:"https://upload.wikimedia.org/wikipedia/commons/thumb/0/09/Coat_of_arms_of_Kinshasa.svg/100px-Coat_of_arms_of_Kinshasa.svg.png", url:"https://www.kinshasa.cd" },
];

export default function Footer() {
  const navigate = useNavigate();
  const { lang } = useLang();
  const tx = t[lang];
  const handleNav = (href: string) => {
    if (href.startsWith("/#")) { navigate("/"); setTimeout(() => { const el = document.querySelector(href.slice(1)); if (el) el.scrollIntoView({ behavior:"smooth" }); }, 200); }
    else { navigate(href); window.scrollTo({ top:0 }); }
  };
  const navLinks = [
    { label:tx.nav.home, href:"/" }, { label:tx.nav.foundress, href:"/fondatrice" },
    { label:tx.nav.humanitarian, href:"/actions" }, { label:tx.nav.ceremonies, href:"/ceremonies" },
    { label:tx.nav.team, href:"/equipe" }, { label:tx.nav.gallery, href:"/galerie" },
  ];
  const domains = [tx.domains.education, tx.domains.health, tx.domains.entrepreneurship, tx.domains.humanitarian, tx.domains.talents];

  return (
    <footer className="relative overflow-hidden" style={{ background:"linear-gradient(135deg, oklch(0.22 0.14 210) 0%, oklch(0.15 0.10 210) 100%)" }}>
      <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-sky-400/4 blur-3xl pointer-events-none" />

      {/* ── Partners ── */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <p className="text-center text-white/50 text-xs font-semibold uppercase tracking-[0.2em] mb-8">{tx.footer.partners}</p>
          <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
            {PARTNERS.map((p) => (
              <a key={p.name} href={p.url} target="_blank" rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 opacity-60 hover:opacity-100 transition-opacity duration-300 group">
                <div className="h-12 flex items-center justify-center">
                  <img src={p.logo} alt={p.name} className="max-h-12 max-w-[120px] object-contain brightness-0 invert group-hover:brightness-100 group-hover:invert-0 transition-all duration-300" />
                </div>
                <span className="text-white/40 text-[10px] font-medium group-hover:text-white/70 transition-colors">{p.name}</span>
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main footer ── */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="py-14 grid md:grid-cols-2 lg:grid-cols-4 gap-10 border-b border-white/10">
          {/* Brand */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-accent flex items-center justify-center"><Heart className="w-5 h-5 text-primary fill-current" /></div>
              <div><p className="text-white font-bold text-lg" style={{ fontFamily:"'Cormorant Garamond', serif" }}>FGNB</p>
                <p className="text-accent text-xs uppercase tracking-[0.12em]">Fondation Gath Naël Beko</p></div>
            </div>
            <p className="text-white/55 text-sm leading-relaxed max-w-sm">Organisation humanitaire engagée pour transformer des vies à travers des actions concrètes. Ensemble, construisons un avenir meilleur.</p>
            <p className="text-accent/70 text-xs italic border-l-2 border-accent/30 pl-3 leading-relaxed">"Ensemble, révélons les talents, redonnons espoir et construisons un avenir meilleur."</p>
            <div className="flex items-center gap-2 pt-1">
              <a href={`mailto:${EMAIL}`} className="text-xs text-white/50 hover:text-accent transition-colors">{EMAIL}</a>
              <span className="text-white/20">·</span>
              <a href={WA} target="_blank" rel="noopener noreferrer" className="text-xs text-green-400 hover:text-green-300 transition-colors">WhatsApp</a>
            </div>
            <div className="flex items-center gap-3 pt-1">
              {[{ Icon:SiFacebook,l:"Facebook" },{ Icon:SiInstagram,l:"Instagram" },{ Icon:SiLinkedin,l:"LinkedIn" }].map(({ Icon, l }) => (
                <button key={l} aria-label={l} className="w-9 h-9 rounded-full bg-white/8 border border-white/12 flex items-center justify-center text-white/60 hover:text-accent hover:border-accent/30 hover:bg-accent/10 transition-all">
                  <Icon className="w-3.5 h-3.5" />
                </button>
              ))}
            </div>
          </div>
          {/* Navigation */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">{tx.footer.navigation}</h4>
            <ul className="space-y-2.5">{navLinks.map(link => (<li key={link.href}><button onClick={() => handleNav(link.href)} className="text-white/55 hover:text-accent text-sm transition-colors">{link.label}</button></li>))}</ul>
          </div>
          {/* Domains */}
          <div>
            <h4 className="text-white font-semibold text-sm mb-5 uppercase tracking-wider">{tx.footer.domains}</h4>
            <ul className="space-y-2.5">{domains.map(d => (<li key={d}><button onClick={() => handleNav("/actions")} className="text-white/55 hover:text-accent text-sm transition-colors text-left">{d}</button></li>))}</ul>
          </div>
        </div>
        <div className="py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/35 text-xs">© {new Date().getFullYear()} Fondation Gath Naël Beko (FGNB). {tx.footer.rights}</p>
          <p className="text-white/25 text-xs flex items-center gap-1.5">{tx.footer.madeWith} <Heart className="w-3 h-3 text-accent fill-current" /> {tx.footer.forWorld}</p>
        </div>
      </div>
    </footer>
  );
}
